import React, { useMemo } from 'react';
import { Avatar, PanelBody, PanelHeader, SidePanel } from '../../kit';
import { ROLE_LABELS } from '../userModel';
import { drillPeople } from './usageModel';

const USAGE_NOUN = { all: 'all usage', app: 'app only', email: 'report emails only' };
const DEVICE_LABEL = { desktop: 'Desktop', mobile: 'Mobile', email: 'Email' };
const fmt = (n) => Math.round(n || 0).toLocaleString('en-US');

/**
 * Who is behind a number: the people a clicked cell, bar or breakdown row
 * counts, most active first. Clicking a person opens their user panel.
 * `selection` = { title, sub?, dayNoun?, ...drillPeople selector }.
 */
export default function UsageDrill({ report, filters, selection, onClose, onOpenUser }) {
  const { rows, inactive } = useMemo(
    () => (selection ? drillPeople(report, filters, selection) : { rows: [], inactive: [] }),
    [report, filters, selection]
  );
  if (!selection) return null;

  const hour = selection.kind === 'hour';
  // hour cells and calendar days only know who was active and on what
  const deviceOnly = hour || selection.kind === 'calendar';
  const daysLabel = hour ? 'Days in this hour' : (selection.dayNoun || 'Active days');
  const context = selection.kind === 'channel' || selection.kind === 'report'
    ? null
    : USAGE_NOUN[filters.usage] || USAGE_NOUN.all;
  const sub = [
    `${fmt(rows.length)} ${rows.length === 1 ? 'person' : 'people'} active`,
    selection.sub,
    context,
  ].filter(Boolean).join(' · ');

  return (
    <SidePanel open onRequestClose={onClose} label={selection.title} narrow>
      <PanelHeader title={selection.title} sub={<span>{sub}</span>} onClose={onClose} bordered />
      <PanelBody>
        {rows.length ? (
          <table className="ak-tbl uu-drill-tbl">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col" className="ak-right">{daysLabel}</th>
                <th scope="col" className="ak-right">{deviceOnly ? 'Devices' : 'App events'}</th>
                {!deviceOnly ? <th scope="col" className="ak-right">Email opens</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user.id} className="ak-click" onClick={() => onOpenUser(r.user.id)}>
                  <td>
                    <div className="ak-cell-id">
                      <Avatar name={r.user.name} email={r.user.email} />
                      <div>
                        <button type="button" className="us-rowlink" onClick={(e) => { e.stopPropagation(); onOpenUser(r.user.id); }}>
                          {r.user.name}
                        </button>
                        <span className="ak-cell-sub">
                          {[ROLE_LABELS[r.user.role] || r.user.role, r.user.jobTitle].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="ak-right">{fmt(r.days)}</td>
                  {deviceOnly ? (
                    <td className="ak-right uu-drill-devices">{r.devices.map((d) => DEVICE_LABEL[d]).join(', ')}</td>
                  ) : (
                    <>
                      <td className="ak-right">{r.events ? fmt(r.events) : <span className="ak-muted">—</span>}</td>
                      <td className="ak-right">{r.opens ? fmt(r.opens) : <span className="ak-muted">—</span>}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="ak-empty"><strong>No one active here</strong>Try a different cell or clear the filters.</div>
        )}

        {inactive.length ? (
          <div className="uu-drill-inactive">
            <span className="ak-section-label">Not active in this period · {fmt(inactive.length)}</span>
            <div className="uu-drill-names">
              {inactive.map((u) => (
                <button key={u.id} type="button" className="ak-chip" onClick={() => onOpenUser(u.id)}>{u.name}</button>
              ))}
            </div>
          </div>
        ) : null}
      </PanelBody>
    </SidePanel>
  );
}
